import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const DATE_FORMATS = [
  'YYYY-MM-DD',
  'YYYY/MM/DD',
  'YYYY-MM',
  'MM/DD',
  'YYYY-MM-DD HH',
  'YYYY.MM.DD',
  'YYYY.MM.DD HH',
  'YY-MM-DD'
];

const clamp = (value, min, max) => {
  if (value == null || Number.isNaN(value)) return min;
  return Math.max(min, Math.min(value, max));
};

const roundValue = (value) => {
  if (value == null || Number.isNaN(value)) return null;
  return Number.parseFloat(Number(value).toFixed(2));
};

const parseLabelAsDate = (label) => {
  if (typeof label !== 'string') return null;
  for (const format of DATE_FORMATS) {
    const parsed = dayjs(label, format, true);
    if (parsed.isValid()) {
      return parsed;
    }
  }
  const isoParsed = dayjs(label);
  return isoParsed.isValid() ? isoParsed : null;
};

const detectStep = (lastLabel, prevLabel, range) => {
  const lastDate = parseLabelAsDate(lastLabel);
  const prevDate = parseLabelAsDate(prevLabel);

  if (lastDate && prevDate) {
    const diffMinutes = lastDate.diff(prevDate, 'minute');
    if (diffMinutes !== 0) {
      return { type: 'date', diffMinutes };
    }
  }

  if (range === 'day' && /^\d{1,2}$/.test(lastLabel) && /^\d{1,2}$/.test(prevLabel)) {
    const diff = Number(lastLabel) - Number(prevLabel);
    const step = diff === 0 ? 1 : diff;
    return { type: 'number-hour', step };
  }

  if (/^\d+$/.test(lastLabel) && /^\d+$/.test(prevLabel)) {
    const diff = Number(lastLabel) - Number(prevLabel);
    const step = diff === 0 ? 1 : diff;
    return { type: 'number', step };
  }

  return { type: 'index', step: 1 };
};

const buildNextLabel = (lastLabel, meta, stepIndex) => {
  switch (meta.type) {
    case 'date': {
      const lastDate = parseLabelAsDate(lastLabel);
      if (!lastDate) break;
      const next = lastDate.add(meta.diffMinutes * stepIndex, 'minute');
      return next.format('YYYY-MM-DD');
    }
    case 'number-hour':
    case 'number': {
      const base = Number(lastLabel);
      if (Number.isNaN(base)) break;
      return String(base + meta.step * stepIndex);
    }
    default:
      break;
  }
  return `예측 ${stepIndex}`;
};

const getForecastHorizon = (length, range) => {
  if (length < 2) return 0;
  const base = Math.max(2, Math.round(length * 0.3));
  switch (range) {
    case 'day':
      return clamp(base, 2, 8);
    case 'week':
      return clamp(base, 2, 7);
    case 'month':
      return clamp(base, 2, 6);
    default:
      return clamp(base, 2, 10);
  }
};

/**
 * 가중 선형회귀 분석 - 최근 데이터에 더 높은 가중치 부여
 * 지수적 감소 가중치를 사용하여 최근 데이터의 영향력을 증가시킴
 * @param {Array} pairs - {x, y} 형태의 데이터 포인트 배열
 * @param {number} alpha - 가중치 감소율 (기본값: 0.3)
 * @returns {Object} {slope, intercept, r2, rmse, mae, confidence}
 */
const computeWeightedLinearRegression = (pairs, alpha = 0.3) => {
  const n = pairs.length;
  if (n < 2) {
    const averageY = pairs.length > 0 ? pairs[0].y : 0;
    return {
      slope: 0,
      intercept: averageY,
      r2: 0,
      rmse: 0,
      mae: 0,
      confidence: 'low'
    };
  }

  const maxX = Math.max(...pairs.map(p => p.x));

  // 가중치 계산: 최근 데이터일수록 높은 가중치
  const weightedPairs = pairs.map(pair => ({
    ...pair,
    weight: Math.exp(-alpha * (maxX - pair.x))
  }));

  const sumW = weightedPairs.reduce((acc, item) => acc + item.weight, 0);
  const sumWX = weightedPairs.reduce((acc, item) => acc + item.weight * item.x, 0);
  const sumWY = weightedPairs.reduce((acc, item) => acc + item.weight * item.y, 0);
  const sumWXY = weightedPairs.reduce((acc, item) => acc + item.weight * item.x * item.y, 0);
  const sumWX2 = weightedPairs.reduce((acc, item) => acc + item.weight * item.x * item.x, 0);

  const denominator = sumW * sumWX2 - sumWX * sumWX;
  if (denominator === 0) {
    const weightedAvgY = sumWY / sumW;
    return {
      slope: 0,
      intercept: weightedAvgY,
      r2: 0,
      rmse: 0,
      mae: 0,
      confidence: 'low'
    };
  }

  const slope = (sumW * sumWXY - sumWX * sumWY) / denominator;
  const intercept = (sumWY - slope * sumWX) / sumW;

  // 통계적 지표 계산
  const predictions = pairs.map(pair => slope * pair.x + intercept);
  const actualValues = pairs.map(pair => pair.y);

  // R² (결정계수) 계산
  const meanY = actualValues.reduce((sum, y) => sum + y, 0) / n;
  const totalSumSquares = actualValues.reduce((sum, y) => sum + Math.pow(y - meanY, 2), 0);
  const residualSumSquares = actualValues.reduce((sum, y, i) => sum + Math.pow(y - predictions[i], 2), 0);
  const r2 = totalSumSquares === 0 ? 1 : Math.max(0, 1 - (residualSumSquares / totalSumSquares));

  // RMSE (평균제곱근오차) 계산
  const rmse = Math.sqrt(residualSumSquares / n);

  // MAE (평균절대오차) 계산
  const mae = actualValues.reduce((sum, y, i) => sum + Math.abs(y - predictions[i]), 0) / n;

  // 신뢰도 등급 계산
  let confidence = 'low';
  if (r2 >= 0.8 && n >= 5) confidence = 'high';
  else if (r2 >= 0.6 && n >= 3) confidence = 'medium';

  return { slope, intercept, r2, rmse, mae, confidence };
};

/**
 * 백테스팅을 통한 모델 성능 검증
 * @param {Array} pairs - 전체 데이터 포인트
 * @param {number} testSize - 테스트용 데이터 포인트 수
 * @returns {Object} 백테스팅 결과
 */
const validateForecastAccuracy = (pairs, testSize = Math.min(3, Math.floor(pairs.length * 0.3))) => {
  if (pairs.length < testSize + 2) {
    return { isValid: false, accuracy: 0, message: 'Insufficient data for validation' };
  }

  const trainData = pairs.slice(0, pairs.length - testSize);
  const testData = pairs.slice(pairs.length - testSize);

  const model = computeWeightedLinearRegression(trainData);

  const predictions = testData.map(point => model.slope * point.x + model.intercept);
  const actualValues = testData.map(point => point.y);

  // MAPE (평균절대백분율오차) 계산
  const mape = actualValues.reduce((sum, actual, i) => {
    if (actual === 0) return sum;
    return sum + Math.abs((actual - predictions[i]) / actual);
  }, 0) / actualValues.filter(v => v !== 0).length;

  const accuracy = Math.max(0, 1 - mape);

  return {
    isValid: true,
    accuracy,
    mape,
    trainSize: trainData.length,
    testSize,
    message: `Accuracy: ${(accuracy * 100).toFixed(1)}%`
  };
};

// 기존 함수를 호환성을 위해 유지하되 새로운 함수를 사용하도록 수정
const computeLinearRegression = (pairs) => {
  const result = computeWeightedLinearRegression(pairs);
  return { slope: result.slope, intercept: result.intercept };
};

export const enhanceKpiDataWithForecast = (kpiItem, range) => {
  if (!kpiItem) return kpiItem;

  const rawChartData = kpiItem.chartData;
  if (!rawChartData) return kpiItem;

  let parsedData;
  try {
    parsedData = typeof rawChartData === 'string' ? JSON.parse(rawChartData) : rawChartData;
  } catch (error) {
    console.warn('Failed to parse chartData for KPI forecast:', error);
    return kpiItem;
  }

  if (!Array.isArray(parsedData) || parsedData.length < 2) {
    return { ...kpiItem, chartData: parsedData };
  }

  const dataKeys = Object.keys(parsedData[0]).filter((key) => key !== 'name' && !key.startsWith('__'));
  if (dataKeys.length === 0) {
    return { ...kpiItem, chartData: parsedData };
  }

  const lastActualIndex = parsedData.length - 1;
  const lastLabel = parsedData[lastActualIndex].name;
  const prevLabel = parsedData[lastActualIndex - 1]?.name ?? parsedData[0].name;
  const labelMeta = detectStep(lastLabel, prevLabel, range || kpiItem.range);

  const horizon = getForecastHorizon(parsedData.length, range || kpiItem.range);
  if (horizon === 0) {
    return { ...kpiItem, chartData: parsedData };
  }

  const enhancedData = parsedData.map((item, idx) => ({ ...item }));
  const seriesMeta = {};

  dataKeys.forEach((metricKey) => {
    const cleaned = parsedData
      .map((item, index) => {
        const value = Number(item[metricKey]);
        return Number.isFinite(value) ? { x: index, y: value } : null;
      })
      .filter(Boolean);

    if (cleaned.length < 2) {
      return;
    }

    // 향상된 가중 선형회귀 분석 사용
    const regressionResult = computeWeightedLinearRegression(cleaned);
    const { slope, intercept, r2, rmse, mae, confidence } = regressionResult;

    // 백테스팅을 통한 모델 검증
    const validation = validateForecastAccuracy(cleaned);

    const forecastKey = `forecast_${metricKey}`;

    // 예측 품질이 낮은 경우 보수적 예측 적용
    const qualityAdjustment = confidence === 'low' ? 0.8 : confidence === 'medium' ? 0.9 : 1.0;

    // attach forecast seed on existing data
    enhancedData.forEach((entry, idx) => {
      if (idx === lastActualIndex) {
        const seedValue = Number(entry[metricKey]);
        entry[forecastKey] = Number.isFinite(seedValue) ? roundValue(Math.max(0, seedValue)) : null;
      } else {
        entry[forecastKey] = null;
      }
    });

    for (let stepIndex = 1; stepIndex <= horizon; stepIndex += 1) {
      const forecastX = lastActualIndex + stepIndex;
      // 품질 조정이 적용된 예측값 계산
      const rawForecastValue = slope * forecastX + intercept;
      const adjustedForecastValue = rawForecastValue * qualityAdjustment;
      const forecastValue = roundValue(Math.max(0, adjustedForecastValue));
      const label = buildNextLabel(lastLabel, labelMeta, stepIndex);

      let point = enhancedData[lastActualIndex + stepIndex];
      if (!point) {
        point = { name: label };
        dataKeys.forEach((key) => {
          point[key] = null;
          point[`forecast_${key}`] = null;
        });
        enhancedData.push(point);
      }

      point.name = label;
      point[metricKey] = null;
      point[forecastKey] = forecastValue;
    }

    const startIndex = enhancedData.findIndex(
      (item, idx) => idx > lastActualIndex && typeof item[forecastKey] === 'number'
    );

    // 통계적 검증 정보를 포함한 메타데이터
    seriesMeta[metricKey] = {
      forecastKey,
      startIndex: startIndex === -1 ? null : startIndex,
      lastActualIndex,
      // 새로 추가된 통계 정보
      statistics: {
        r2: roundValue(r2),
        rmse: roundValue(rmse),
        mae: roundValue(mae),
        confidence,
        qualityAdjustment,
        dataPoints: cleaned.length
      },
      validation: validation.isValid ? {
        accuracy: roundValue(validation.accuracy),
        mape: roundValue(validation.mape),
        trainSize: validation.trainSize,
        testSize: validation.testSize
      } : null
    };
  });

  return {
    ...kpiItem,
    chartData: enhancedData,
    forecastMeta: {
      series: seriesMeta,
      labelMeta,
      horizon
    }
  };
};

export const enhanceKpiCollectionWithForecast = (collection, filtersByKey = {}) => {
  if (!Array.isArray(collection)) return collection;

  return collection.map((item) => {
    const key = `${item.categoryCd}|${item.kpiIndicatorCd}`;
    const range = filtersByKey[key]?.range || filtersByKey[key]?.dateRange || item.range;
    return enhanceKpiDataWithForecast(item, range);
  });
};
